import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useIntl } from 'react-intl';

import Button from '@atlaskit/button/default/button';
import AppsIcon from '@atlaskit/icon/core/apps';
import ModalTransition from '@atlaskit/modal-dialog/modal-transition';
import SectionMessage from '@atlaskit/section-message/message';

import { usePostOfficeUserInteractions } from '@atlassian/post-office-analytics/interactions';
import { useFirePostOfficeAnalyticsEvents } from '@post-office/analytics';
import { useMessageEvent } from '@post-office/message-lifecycle';

import type { TwcDiscoveryApp } from '../../..';

import { messages } from './messages';

import type { MessageFrontendProps } from './types';

import { preloadCommerceExperience, TwcDiscoveryModal } from './twc-discovery-modal';

const TWC_APPS: TwcDiscoveryApp[] = ['jira', 'confluence', 'loom'];

// Product names are trademarks, so they aren't translated.
const APP_NAMES: Record<TwcDiscoveryApp, string> = {
	jira: 'Jira',
	confluence: 'Confluence',
	loom: 'Loom',
};

export const AdminhubBillingPreviewTwcDiscoveryMessage: (
	props: MessageFrontendProps,
) => JSX.Element = ({ experienceUrl, variant, ownedApps, edition, onDismiss }) => {
	const intl = useIntl();
	const { onCtaActioned, onClosed } = usePostOfficeUserInteractions();
	const { messageDismissed } = useMessageEvent();
	const { fireAnalyticsEvent } = useFirePostOfficeAnalyticsEvents();

	// A dedicated screen event, unlike the shared messageInstance viewed, can drive a real-time TAP trait.
	const hasReportedView = useRef(false);
	useEffect(() => {
		if (hasReportedView.current) {
			return;
		}
		hasReportedView.current = true;
		fireAnalyticsEvent({
			eventType: 'screen',
			name: 'twcDiscoveryBanner',
			attributes: { variant, edition, ownedApps },
		});
	}, [edition, fireAnalyticsEvent, ownedApps, variant]);

	const { title, body } = useMemo(() => {
		// Apps are always listed in TwC hierarchy order: Jira > Confluence > Loom.
		const owned = TWC_APPS.filter((app) => ownedApps.includes(app)).map((app) => APP_NAMES[app]);
		const missing = TWC_APPS.filter((app) => !ownedApps.includes(app)).map((app) => APP_NAMES[app]);
		const ownedList = intl.formatList(owned, { type: 'conjunction' });
		const benefits = intl.formatList([...missing, intl.formatMessage(messages.moreAiCredits)], {
			type: 'conjunction',
		});

		switch (variant) {
			case 'multi-app-price-increase':
				return {
					title: intl.formatMessage(messages.multiAppPriceIncreaseTitle, { edition }),
					body: intl.formatMessage(messages.multiAppPriceIncreaseBody, {
						ownedApps: ownedList,
						benefits,
					}),
				};
			case 'price-saving':
				return {
					title: intl.formatMessage(messages.priceSavingTitle, { ownedApps: ownedList, edition }),
					body: intl.formatMessage(messages.priceSavingBody, { benefits }),
				};
			case 'single-app':
			default:
				return {
					title: intl.formatMessage(messages.singleAppTitle, {
						benefits,
						ownedApp: owned[0],
					}),
					body: intl.formatMessage(messages.singleAppBody),
				};
		}
	}, [edition, intl, ownedApps, variant]);

	const [isModalOpen, setIsModalOpen] = useState(false);
	const handleModalClose = useCallback(() => setIsModalOpen(false), []);

	const handleCtaClick = useCallback(() => {
		setIsModalOpen(true);
		void onCtaActioned({
			interactionIdentifier: 'explore-teamwork-collection',
			destinationUrl: experienceUrl,
			additionalMetadata: { variant },
		});
	}, [experienceUrl, onCtaActioned, variant]);

	const handleDismiss = useCallback(() => {
		void onClosed({
			interactionIdentifier: 'twc-discovery-banner-dismissed',
			additionalMetadata: { variant },
		});

		void messageDismissed();

		onDismiss?.();
	}, [messageDismissed, onClosed, onDismiss, variant]);

	return (
		<>
			<SectionMessage
				appearance="discovery"
				icon={AppsIcon}
				title={title}
				actions={
					<Button
						onClick={handleCtaClick}
						onMouseEnter={preloadCommerceExperience}
						onFocus={preloadCommerceExperience}
					>
						{intl.formatMessage(messages.cta)}
					</Button>
				}
				isDismissible
				onDismiss={handleDismiss}
			>
				{body}
			</SectionMessage>
			<ModalTransition>
				{isModalOpen && (
					<TwcDiscoveryModal
						experienceUrl={experienceUrl}
						variant={variant}
						onClose={handleModalClose}
					/>
				)}
			</ModalTransition>
		</>
	);
};
